import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import TableManager from '../components/data/TableManager';
import { FaClipboardCheck, FaUser } from 'react-icons/fa';

// CONFIGURAZIONE COLONNE: Basata sulla DDL SKE_TestModifiche
const temColumnConfig = [
  // Fields not shown in forms or table (visibility 0) 
  "TEM_Id_TestModifiche=0,ID",
  "TEM_IdRif_TestModifiche=0,ID Rif.",
  
  // IMPORTANTE: Campo collegamento alla macro funzione (ora visibile in tabella per il lookup)
  "TEM_Id_MacroFunzioni=2,Macro Funzione,lookupName=macrofunzionilookup,required",
  "TEM_Id_Richieste=1,Richiesta,lookupName=richiestelookup",
  
  // General form fields (visibility 2 = form + tabella)
  "TEM_DescrizioneModifica=2,Descrizione Modifica,required", 
  "TEM_CriterioTest=2,Criterio Test,required", 
  
  // Test result field (visibility 2 = form + tabella)
  "TEM_TestSuperato=1,Test Superato,isBoolean=true",
  
  // IMPORTANTE: Campo esecutore (nascosto perché filtrato automaticamente)
  "TEM_IdEsecutore_Utenti=0,Utente Esecutore,lookupName=utentiLookup",
  
  // User fields (visibility 1 = solo form, con lookup verso utenti)
  "TEM_IdRichiedente_Utenti=1,Utente Richiedente,required,lookupName=utentiLookup",
  
  // System fields (visibility 0 = nascosti ma gestiti dal backend)
  "TEM_InizioValidita=0,Inizio Validità,type=datetime", 
  "TEM_FineValidita=0,Fine Validità,type=datetime", 
  "TEM_StatoRecord=0,Stato Record", 

  // Audit fields (visibility 0 = nascosti, gestiti dal backend)
  "TEM_UtenteInserimento=0,Utente Inserimento",
  "TEM_DataOraInserimento=0,Data/Ora Inserimento",
  "TEM_TerminaleInserimento=0,Terminale Inserimento",
  "TEM_UtenteModifica=0,Utente Modifica",
  "TEM_DataOraModifica=0,Data/Ora Modifica",
  "TEM_TerminaleModifica=0,Terminale Modifica",
  "TEM_Timestamp=0,Timestamp DB",
];

// CONFIGURAZIONE LAYOUT: Ottimizzato per la pagina (senza possibilità di creare)
const temFormLayoutConfig = {
  sections: [
    {
      title: "Dettagli Test",
      fields: ["TEM_DescrizioneModifica", "TEM_CriterioTest"]
    },
    {
      title: "Collegamenti",
      layout: "twoColumns",
      columns: [
        {
          title: "Macro Funzione",
          icon: "FaCog",
          fields: [["TEM_Id_MacroFunzioni"]]
        },
        {
          title: "Richiesta",
          icon: "FaFileAlt",
          fields: [["TEM_Id_Richieste"]]
        }
      ]
    },
    {
      title: "Risultati e Responsabilità",
      layout: "twoColumns",
      columns: [
        {
          title: "Risultato Test",
          icon: "FaCheckCircle",
          fields: [["TEM_TestSuperato"]]
        },
        {
          title: "Utente Richiedente", 
          icon: "FaUser",
          fields: [["TEM_IdRichiedente_Utenti"]]
        }
      ]
    }
  ]
};

// AGGIORNATO: Configurazione lookup con MAC_Stato per row coloring
const temLookupConfig = {
  utentiLookup: {
    tableName: 'SKE_Utenti',
    valueField: 'UTE_Id_Utenti', 
    labelField: 'UTE_NomeUtente', 
    additionalFields: ['UTE_Email'],
  },
  richiestelookup: {
    tableName: 'SKE_Richieste',
    valueField: 'RIC_Id_Richieste', 
    labelField: 'RIC_DescrizioneRichiesta',
  },
  // AGGIORNATO: Lookup macro funzioni con MAC_Stato per row coloring
  macrofunzionilookup: {
    tableName: 'SKE_MacroFunzioni',
    valueField: 'MAC_Id_MacroFunzioni',
    labelField: 'MAC_Nome',
    additionalFields: ['MAC_Descrizione', 'MAC_Priorita', 'MAC_Stato'], // AGGIUNTO: MAC_Stato per row coloring
  }
};

const temInputTypes = {
  // Campi visibili
  TEM_DescrizioneModifica: 'textarea',
  TEM_CriterioTest: 'textarea',
  
  // Campo booleano
  TEM_TestSuperato: 'boolean',
  
  // Lookup fields
  TEM_IdRichiedente_Utenti: 'select',
  TEM_IdEsecutore_Utenti: 'select',
  TEM_Id_Richieste: 'select',
  TEM_Id_MacroFunzioni: 'select',
  
  // Campi nascosti (per il backend)
  TEM_InizioValidita: 'datetime-local',
  TEM_FineValidita: 'datetime-local',
  TEM_StatoRecord: 'select',
};

// IMPORTANTE: Configurazione filtri vuota per disabilitare la ricerca manuale
const temFilterConfig = [];

// NUOVO: Componente wrapper per gestire l'ordinamento frontend
const SortedTableManager = ({ lookupOptions, ...props }) => {
  // Funzione per ordinare i dati in base alla priorità della macro funzione
  const sortByMacroFunctionPriority = useMemo(() => {
    return (data) => {
      if (!data || !Array.isArray(data) || !lookupOptions?.macrofunzionilookup) {
        return data;
      }




      // Crea una mappa delle priorità per ID macro funzione
      const priorityMap = new Map();
      lookupOptions.macrofunzionilookup.forEach(macro => {
        priorityMap.set(macro.value, {
          priority: macro.MAC_Priorita || 999, // Default alta priorità se manca
          name: macro.label || macro.MAC_Nome
        });
      });



      // Ordina i test per priorità della macro funzione associata
      const sortedData = [...data].sort((a, b) => {
        const priorityA = priorityMap.get(a.TEM_Id_MacroFunzioni)?.priority || 999;
        const priorityB = priorityMap.get(b.TEM_Id_MacroFunzioni)?.priority || 999;
        

        
        // Priorità più bassa = più importante (1 = alta priorità)
        return priorityA - priorityB;
      });


      return sortedData;
    };
  }, [lookupOptions?.macrofunzionilookup]);

  // Passa la funzione di ordinamento al TableManager
  return (
    <TableManager
      {...props}
      lookupOptions={lookupOptions}
      customDataSorter={sortByMacroFunctionPriority}
    />
  );
};

const TestModifichePage = () => {
  const { user } = useAuth();
  const [pageKey, setPageKey] = useState(0);

  // Effetto per ricaricare la pagina quando cambia l'utente
  useEffect(() => {
    if (user?.id) {
      setPageKey(prev => prev + 1);
    }
  }, [user?.id]);

  // Se non c'è utente, mostra caricamento
  if (!user?.id) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-500 mx-auto"></div>
          <p className="mt-4 text-gray-600 dark:text-gray-300">Caricamento utente...</p>
        </div>
      </div>
    );
  }

  // **CORREZIONE CRITICA**: Filtro preimpostato con i valori corretti per il backend
  const initialFiltersForTEM = { 
    TEM_IdEsecutore_Utenti: user.id,
    // **IMPORTANTE**: Per campi boolean nel backend, usa 0/1 invece di true/false
    TEM_TestSuperato: 0 // 0 = false, 1 = true nel database
  };



  return (
    <div className="container mx-auto px-4 py-6">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg">
        <div className="p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center space-x-3">
            <FaClipboardCheck className="text-2xl text-emerald-600 dark:text-violet-400" />
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                I Miei Test Modifiche
              </h1>
              <p className="text-gray-600 dark:text-gray-300">
                Test modifiche assegnati a <span className="font-semibold">{user.username}</span>
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                Solo test non superati, ordinati per priorità della macro funzione
              </p>
            </div>
          </div>
        </div>
        
        <div className="p-6">
          <SortedTableManager
            key={`test-modifiche-user-${user.id}-${pageKey}`}
            tableName="SKE_TestModifiche"
            primaryKey="TEM_Id_TestModifiche"
            pageTitle=""
            columnConfig={temColumnConfig}
            filterConfig={temFilterConfig} // Array vuoto = nessuna barra di ricerca
            lookupConfig={temLookupConfig}
            inputTypes={temInputTypes}
            initialFilters={initialFiltersForTEM}
            singularRecordName="Test Modifica"
            // MODIFICATO: Disabilitata la creazione
            enableCreate={false}
            enableEdit={true}
            enableDelete={true}
            formLayoutConfig={temFormLayoutConfig}
            // **CORREZIONE**: Usa la proprietà corretta per la ricerca automatica
            autoSearchOnInitialFilters={true}
            // AGGIUNTO: Status field per row coloring come MacroFunzioniPage
            statusField="MAC_Stato"
          />
        </div>
      </div>
    </div>
  );
};

export default TestModifichePage;